/**
 * VENOA sticky CTA.
 *
 * The pill is pure navigation: it scrolls back to the buy area so the scent
 * and the single/bundle choice are made there, deliberately. It never adds
 * anything to the cart.
 *
 * Visibility is driven by an IntersectionObserver over the core of the buy
 * area (scent picker, buy-mode switch, buy buttons). Its root reaches far below
 * the screen, so a part counts as "present" while it is on screen OR still
 * ahead. Only once all of them lie above the screen – the visitor has scrolled
 * past the buy area – does the pill fade in; at the top of the page (short
 * phone screens) it stays hidden. Jumps over the buy area are caught too.
 * The same pill serves single and 2+1 mode (venoa-buy-mode.js fills in the
 * bundle price).
 */
class VenoaStickyCta extends HTMLElement {
  // The parts that actually make up "choosing and buying", in scroll order.
  static CORE_SELECTOR = 'variant-picker, venoa-buy-mode, .buy-buttons-block';

  connectedCallback() {
    this.pill = this.querySelector('[data-sticky-pill]');
    this.button = this.querySelector('[data-sticky-scroll]');
    this.region =
      this.closest('.shopify-section')?.querySelector('[data-venoa-purchase]') ??
      document.querySelector('[data-venoa-purchase]');
    if (!this.pill || !this.button || !this.region) return;

    this.targets = Array.from(this.region.querySelectorAll(VenoaStickyCta.CORE_SELECTOR));
    if (!this.targets.length) this.targets = [this.region];

    this.controller = new AbortController();
    this.button.addEventListener('click', this.handleClick, { signal: this.controller.signal });

    this.present = new Set();
    this.observer = new IntersectionObserver(this.handleIntersect, {
      threshold: 0,
      rootMargin: '0px 0px 100000px 0px',
    });
    this.targets.forEach((target) => this.observer.observe(target));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  handleIntersect = (entries) => {
    // Hidden parts (buy buttons in 2+1 mode) have no box and never count.
    for (const entry of entries) {
      if (entry.isIntersecting) this.present.add(entry.target);
      else this.present.delete(entry.target);
    }
    this.setShown(this.present.size === 0);
  };

  // Showing and hiding is a single attribute flip; the CSS owns the rest,
  // including making the hidden pill untouchable.
  setShown(shown) {
    if (shown === this.shown) return;
    this.shown = shown;
    this.pill.dataset.stuck = shown ? 'true' : 'false';
  }

  handleClick = () => {
    // In 2+1 mode the bottles are chosen in the bundle card, so land there.
    const bundle = this.closest('.shopify-section')?.dataset.venoaMode === 'bundle';
    const target = (bundle && this.targets.find((el) => el.matches('venoa-buy-mode'))) || this.targets[0];
    if (!target) return;

    // Only a sticky header eats space at the top; a static one does not.
    const header = document.querySelector('#header-component');
    const stuckHeader = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--venoa-cta-scroll-offset', `${stuckHeader}px`);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  };
}

if (!customElements.get('venoa-sticky-cta')) {
  customElements.define('venoa-sticky-cta', VenoaStickyCta);
}
